/**
 * EPM — Entity Consolidation.
 *
 * One container page with 6 tabs:
 *   1. Chart of Accounts (segments + qualifiers)
 *   2. Segment Values
 *   3. Ledgers
 *   4. Trial Balances (CSV upload + manual entry + Oracle stub)
 *   5. Consolidation Setup + Elimination Rules
 *   6. Run + Output
 */
import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { PageHeader } from "@/components/layout/PageHeader";
import { Plus, Trash, Play, UploadSimple, CheckCircle } from "@phosphor-icons/react";
import PlanTab from "./PlanTab";

const QUALIFIERS = ["Company", "Cost Center", "Intercompany", "Account", "Sub-Account", "Reporting"];
const SOURCES = ["manual", "fusion", "ebs"];

export default function EpmPage() {
  const [tab, setTab] = useState("coa");

  return (
    <div>
      <PageHeader eyebrow="EPM" title="Entity Consolidation"
        subtitle="Define your chart of accounts, ledgers, trial balances, consolidation rules, and run multi-currency consolidations with intercompany eliminations."
        testid="epm-title" />
      <div className="px-8 pt-4 flex flex-wrap gap-1 bg-gradient-to-b from-white to-gray-50 border-b border-slate-200" data-testid="epm-tabs">
        {[
          ["coa", "1 · Chart of Accounts"], ["values", "2 · Segment Values"],
          ["ledgers", "3 · Ledgers"], ["tb", "4 · Trial Balances"],
          ["cons", "5 · Consolidation Setup"], ["run", "6 · Run & Output"],
          ["plan", "7 · Planning & Budgeting"],
        ].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`epm-tab-${k}`}
            className={`px-4 py-2.5 text-[12px] font-semibold tracking-wide border-b-2 transition-colors ${
              tab === k
                ? "border-[#C8A24B] text-[#0B1E3F] bg-white"
                : "border-transparent text-slate-500 hover:text-slate-800 hover:bg-white/60"
            }`}>{l}</button>
        ))}
      </div>
      <div className="p-8">
        {tab === "coa" && <CoaTab />}
        {tab === "values" && <ValuesTab />}
        {tab === "ledgers" && <LedgerTab />}
        {tab === "tb" && <TbTab />}
        {tab === "cons" && <ConsTab />}
        {tab === "run" && <RunTab />}
        {tab === "plan" && (
          <div className="space-y-6">
            <div className="rounded-sm border border-[#0B1E3F]/20 bg-[#0B1E3F]/5 px-5 py-4 flex flex-wrap items-center justify-between gap-3" data-testid="epm-epbcs-banner">
              <div>
                <div className="font-bold tracking-tight text-[#0B1E3F]">EPBCS Suite — full Planning &amp; Budgeting module</div>
                <p className="text-[12px] text-slate-600 mt-0.5">Setup Masters, Role Matrix, Revenue, OpEx, CapEx, Workforce and Budgeted Financials now live as a dedicated module.</p>
              </div>
              <a href="/planning/setup" data-testid="epm-open-epbcs-link"
                className="bg-[#0B1E3F] text-white px-4 py-2 text-[11px] font-bold uppercase tracking-wider rounded">
                Open EPBCS Suite →
              </a>
            </div>
            <PlanTab />
          </div>
        )}
      </div>
    </div>
  );
}

// --------------------------------------------------------------------- CoA --
const CoaTab = () => {
  const [items, setItems] = useState([]);
  const [draft, setDraft] = useState({ name: "", source: "manual",
    segments: [{ key: "company", label: "Company", qualifier: "Company", order: 1 }] });
  const [pull, setPull] = useState({ loading: false, result: null, error: null });
  const load = () => api.get("/epm/coa").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  const save = async () => {
    await api.post("/epm/coa", draft);
    setDraft({ name: "", source: "manual", segments: [{ key: "company", label: "Company", qualifier: "Company", order: 1 }] });
    load();
  };
  const pullFromOracle = async () => {
    setPull({ loading: true, result: null, error: null });
    try {
      const { data } = await api.post("/epm/coa/pull-oracle", {});
      setPull({ loading: false, result: data, error: null });
      load();
    } catch (e) {
      setPull({ loading: false, result: null,
                error: e?.response?.data?.detail || e.message });
    }
  };
  const addSeg = () => setDraft({ ...draft, segments: [...draft.segments, { key: "", label: "", qualifier: "Account", order: draft.segments.length + 1 }] });
  return (
    <div className="space-y-6">
      <OracleCoaPullCard pull={pull} onPull={pullFromOracle} />
      <div className="rounded-sm border border-slate-200 bg-white shadow-sm p-5" data-testid="coa-editor">
        <h3 className="font-bold tracking-tight mb-3">New / edit chart of accounts</h3>
        <div className="grid grid-cols-2 gap-3 mb-3">
          <Field label="COA name"><input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })}
            data-testid="coa-name" className="w-full border border-slate-300 px-2 py-1.5 text-sm" /></Field>
          <Field label="Source">
            <select value={draft.source} onChange={(e) => setDraft({ ...draft, source: e.target.value })}
              data-testid="coa-source" className="w-full border border-slate-300 px-2 py-1.5 text-sm bg-white">
              {SOURCES.map((s) => <option key={s}>{s}</option>)}
            </select>
          </Field>
        </div>
        <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-slate-500 mb-1">Segments</div>
        {draft.segments.map((s, i) => (
          <div key={i} className="grid grid-cols-[1fr_1fr_1fr_60px_30px] gap-2 mb-2 items-center">
            <input placeholder="key" value={s.key} onChange={(e) => updateSeg(draft, setDraft, i, "key", e.target.value)} className="border border-slate-300 px-2 py-1 text-[12px]" />
            <input placeholder="label" value={s.label} onChange={(e) => updateSeg(draft, setDraft, i, "label", e.target.value)} className="border border-slate-300 px-2 py-1 text-[12px]" />
            <select value={s.qualifier} onChange={(e) => updateSeg(draft, setDraft, i, "qualifier", e.target.value)} className="border border-slate-300 px-2 py-1 text-[12px] bg-white">
              {QUALIFIERS.map((q) => <option key={q}>{q}</option>)}
            </select>
            <input type="number" placeholder="order" value={s.order} onChange={(e) => updateSeg(draft, setDraft, i, "order", +e.target.value)} className="border border-slate-300 px-2 py-1 text-[12px]" />
            <button onClick={() => setDraft({ ...draft, segments: draft.segments.filter((_, j) => j !== i) })} className="text-slate-400 hover:text-[#DC2626]"><Trash size={14} /></button>
          </div>
        ))}
        <div className="flex items-center gap-2 mt-2">
          <button onClick={addSeg} className="text-[11px] font-bold uppercase tracking-wider text-[#0B1E3F] hover:underline inline-flex items-center gap-1"><Plus size={12} /> Add segment</button>
          <button onClick={save} disabled={!draft.name} data-testid="coa-save" className="ml-auto bg-[#0B1E3F] text-white px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider disabled:opacity-60">Save COA</button>
        </div>
      </div>
      <List title="Existing chart of accounts" items={items} render={(c) => (
        <div className="flex items-center justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-sm">{c.name} <span className="text-[10px] text-slate-400 font-mono">{c.source}</span></div>
            <div className="text-[11px] text-slate-500 truncate">{(c.segments || []).map((s) => `${s.label}(${s.qualifier})`).join(" · ")}</div>
          </div>
          <button onClick={async () => { await api.delete(`/epm/coa/${c.id}`); load(); }} className="text-slate-400 hover:text-[#DC2626]"><Trash size={14} /></button>
        </div>
      )} />
    </div>
  );
};
const updateSeg = (draft, setDraft, i, k, v) => {
  const next = [...draft.segments]; next[i] = { ...next[i], [k]: v }; setDraft({ ...draft, segments: next });
};

// ------------------------------------------------------ Oracle Pull Cards --
// Small visual pill used inside the TB pull success card to show how many
// rows shifted vs. the previous pull. Tones are mapped to Tailwind colour
// scales so future palette changes are a one-line edit here.
const DiffPill = ({ label, value, tone }) => {
  const map = {
    emerald: "bg-emerald-100 text-emerald-800 border-emerald-200",
    amber:   "bg-amber-100 text-amber-800 border-amber-200",
    slate:   "bg-slate-100 text-slate-700 border-slate-200",
    red:     "bg-red-100 text-red-800 border-red-200",
  };
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-sm border text-[11px] ${map[tone] || map.slate}`}
          data-testid={`tb-diff-${label}`}>
      <span className="font-mono font-semibold">{value}</span>
      <span className="uppercase tracking-wider text-[9px]">{label}</span>
    </span>
  );
};

// ------------------------------------------------------ TB Monitors Card --
// Saved monitors that re-run on a schedule and post to Slack/Teams when the
// diff exceeds a threshold. Sits below the on-demand pull card on the TB tab.
const TbMonitorsCard = ({ ledgers }) => {
  const [list, setList] = useState([]);
  const [creating, setCreating] = useState(false);
  const blankDraft = {
    name: "", ledger_id: "", period: "", report_url: "", accounts_text: "",
    amount_type: "PTD", currency: "",
    webhook_url: "", webhook_kind: "slack",
    alert_on_new: 0, alert_on_changed: 5, alert_on_removed: 1,
    frequency_minutes: 60, enabled: true,
  };
  const [draft, setDraft] = useState(blankDraft);
  const [runState, setRunState] = useState({});
  const load = () => api.get("/epm/tb-monitors").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);
  const save = async () => {
    if (!draft.name || !draft.ledger_id || !draft.period || !draft.webhook_url) return;
    const accounts = (draft.accounts_text || "")
      .split(/[,\n]+/).map((s) => s.trim()).filter(Boolean);
    const payload = {
      ...draft,
      accounts: accounts.length ? accounts : undefined,
      accounts_text: undefined,
    };
    await api.post("/epm/tb-monitors", payload);
    setCreating(false); setDraft(blankDraft);
    load();
  };
  const remove = async (id) => { await api.delete(`/epm/tb-monitors/${id}`); load(); };
  const runNow = async (m) => {
    setRunState({ ...runState, [m.id]: { loading: true } });
    try {
      const { data } = await api.post(`/epm/tb-monitors/${m.id}/run`);
      setRunState({ ...runState, [m.id]: { loading: false, result: data } });
      load();
    } catch (e) {
      setRunState({ ...runState, [m.id]: { loading: false,
                                              error: e?.response?.data?.detail || e.message } });
    }
  };
  return (
    <div className="rounded-sm border border-slate-200 bg-white shadow-sm p-5"
         data-testid="tb-monitors-card">
      <div className="flex items-center gap-3 mb-3">
        <h3 className="font-bold tracking-tight">TB change-monitors</h3>
        <span className="text-[11px] text-slate-500">
          Auto-re-pull on a schedule and alert Slack / Teams when the TB drifts.
        </span>
        <button onClick={() => setCreating(!creating)}
                data-testid="tb-monitor-new-btn"
                className="ml-auto text-[11px] font-bold uppercase tracking-wider text-[#0B1E3F] inline-flex items-center gap-1">
          <Plus size={12} /> {creating ? "Cancel" : "New monitor"}
        </button>
      </div>
      {creating && (
        <div className="rounded border border-dashed border-[#0B1E3F]/30 p-3 mb-4 space-y-2"
             data-testid="tb-monitor-form">
          <div className="grid grid-cols-3 gap-2">
            <input placeholder="Monitor name" value={draft.name}
                   onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                   className="border border-slate-300 px-2 py-1.5 text-sm" />
            <select value={draft.ledger_id}
                    onChange={(e) => setDraft({ ...draft, ledger_id: e.target.value })}
                    className="border border-slate-300 px-2 py-1.5 text-sm bg-white">
              <option value="">— Ledger —</option>
              {ledgers.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
            </select>
            <input placeholder="period YYYY-MM" value={draft.period}
                   onChange={(e) => setDraft({ ...draft, period: e.target.value })}
                   className="border border-slate-300 px-2 py-1.5 text-sm" />
          </div>
          <input placeholder="Report URL (paste OTBI/BIP) — leave blank to use account list"
                 value={draft.report_url}
                 onChange={(e) => setDraft({ ...draft, report_url: e.target.value })}
                 className="w-full border border-slate-300 px-2 py-1.5 text-[12px] font-mono" />
          <textarea placeholder="OR account combinations, one per line"
                    rows={2} value={draft.accounts_text}
                    onChange={(e) => setDraft({ ...draft, accounts_text: e.target.value })}
                    className="w-full border border-slate-300 px-2 py-1.5 text-[12px] font-mono" />
          <div className="grid grid-cols-3 gap-2">
            <input placeholder="Webhook URL (Slack or Teams)" value={draft.webhook_url}
                   onChange={(e) => setDraft({ ...draft, webhook_url: e.target.value })}
                   className="border border-slate-300 px-2 py-1.5 text-[12px] font-mono" />
            <select value={draft.webhook_kind}
                    onChange={(e) => setDraft({ ...draft, webhook_kind: e.target.value })}
                    className="border border-slate-300 px-2 py-1.5 text-sm bg-white">
              <option value="slack">Slack</option>
              <option value="teams">Microsoft Teams</option>
            </select>
            <input type="number" min="15" placeholder="Every N minutes (≥15)"
                   value={draft.frequency_minutes}
                   onChange={(e) => setDraft({ ...draft, frequency_minutes: +e.target.value })}
                   className="border border-slate-300 px-2 py-1.5 text-sm" />
          </div>
          <div className="grid grid-cols-4 gap-2 items-center">
            <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Alert thresholds</label>
            <input type="number" min="0" placeholder="≥ new" value={draft.alert_on_new}
                   onChange={(e) => setDraft({ ...draft, alert_on_new: +e.target.value })}
                   className="border border-slate-300 px-2 py-1.5 text-sm" />
            <input type="number" min="0" placeholder="≥ changed" value={draft.alert_on_changed}
                   onChange={(e) => setDraft({ ...draft, alert_on_changed: +e.target.value })}
                   className="border border-slate-300 px-2 py-1.5 text-sm" />
            <input type="number" min="0" placeholder="≥ removed" value={draft.alert_on_removed}
                   onChange={(e) => setDraft({ ...draft, alert_on_removed: +e.target.value })}
                   className="border border-slate-300 px-2 py-1.5 text-sm" />
          </div>
          <div className="flex items-center gap-2">
            <label className="text-[11px] inline-flex items-center gap-1.5">
              <input type="checkbox" checked={draft.enabled}
                     onChange={(e) => setDraft({ ...draft, enabled: e.target.checked })}
                     className="accent-[#0B1E3F]" />
              Enabled
            </label>
            <button onClick={save} disabled={!draft.name || !draft.ledger_id || !draft.period || !draft.webhook_url}
                    data-testid="tb-monitor-save-btn"
                    className="ml-auto bg-[#0B1E3F] text-white px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider disabled:opacity-60">
              Save monitor
            </button>
          </div>
        </div>
      )}
      {list.length === 0 ? (
        <div className="text-[12px] text-slate-500">No monitors yet.</div>
      ) : (
        <div className="space-y-2">
          {list.map((m) => {
            const rs = runState[m.id] || {};
            const d = m.last_diff || {};
            return (
              <div key={m.id} className="border border-slate-200 rounded p-3"
                   data-testid={`tb-monitor-row-${m.id}`}>
                <div className="flex items-center gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-sm">{m.name}
                      <span className="ml-2 text-[10px] font-mono text-slate-500">
                        {m.ledger_name} · {m.period} · every {m.frequency_minutes}m
                      </span>
                      {!m.enabled && <span className="ml-2 text-[10px] text-amber-700">disabled</span>}
                    </div>
                    <div className="text-[11px] text-slate-600 truncate">
                      {m.webhook_kind}:{(m.webhook_url || "").slice(0, 60)}…
                      · thresholds: new≥{m.alert_on_new}, chg≥{m.alert_on_changed}, rem≥{m.alert_on_removed}
                    </div>
                    {m.last_run_at && (
                      <div className="text-[10px] text-slate-500 mt-1">
                        Last: {m.last_run_at.slice(0, 19)} · Δ new {d.new || 0} / changed {d.changed || 0} /
                        removed {d.removed || 0}
                        {m.last_alert_at ? " · webhook fired" : (m.last_alert_detail ? ` · ${m.last_alert_detail}` : "")}
                      </div>
                    )}
                    {rs.result && (
                      <div className="text-[10px] text-emerald-700 mt-1">
                        Ran — alert {rs.result.alert?.alerted ? "sent" : "skipped"}
                      </div>
                    )}
                    {rs.error && <div className="text-[10px] text-red-700 mt-1">{rs.error}</div>}
                  </div>
                  <button onClick={() => runNow(m)} disabled={rs.loading}
                          data-testid={`tb-monitor-run-${m.id}`}
                          className="text-[10px] font-bold uppercase tracking-wider text-[#0B1E3F] border border-[#0B1E3F] px-2 py-1 hover:bg-slate-50 disabled:opacity-60">
                    {rs.loading ? "Running…" : "Run now"}
                  </button>
                  <button onClick={() => remove(m.id)} className="text-slate-400 hover:text-[#DC2626]">
                    <Trash size={14} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// Card shown at the top of the COA tab — single click pulls every Fusion COA
// and linked Ledger via the live Oracle Fusion REST API.
const OracleCoaPullCard = ({ pull, onPull }) => (
  <div className="rounded-sm border border-[#0B1E3F]/20 bg-gradient-to-br from-[#0B1E3F]/[0.04] to-white shadow-sm p-5"
       data-testid="coa-oracle-pull">
    <div className="flex items-start gap-4">
      <div className="flex-1">
        <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-[#0B1E3F] mb-1">
          Oracle Fusion · live connector
        </div>
        <h3 className="font-bold tracking-tight mb-1">Pull Chart of Accounts + Ledgers</h3>
        <p className="text-[12px] text-slate-600 leading-relaxed max-w-2xl">
          One click reads every COA structure and primary ledger from your
          configured Fusion instance and upserts them here. Safe to re-run —
          existing records are matched by their Oracle ID and updated in place.
        </p>
      </div>
      <button onClick={onPull} disabled={pull.loading}
              data-testid="coa-pull-oracle-btn"
              className="bg-[#0B1E3F] text-white px-4 py-2 text-[11px] font-bold uppercase tracking-wider disabled:opacity-60 inline-flex items-center gap-2">
        {pull.loading ? "Pulling…" : "Pull from Oracle"}
      </button>
    </div>
    {pull.result && (
      <div className="mt-4 rounded-md bg-emerald-50 border border-emerald-200 p-3 text-[12px]"
           data-testid="coa-pull-success">
        <span className="font-semibold text-emerald-800">Imported </span>
        <span className="font-mono text-emerald-900">{pull.result.coa_count}</span>
        <span className="text-emerald-800"> chart{pull.result.coa_count === 1 ? "" : "s"} of accounts and </span>
        <span className="font-mono text-emerald-900">{pull.result.ledger_count}</span>
        <span className="text-emerald-800"> ledger{pull.result.ledger_count === 1 ? "" : "s"} from Fusion.</span>
      </div>
    )}
    {pull.error && (
      <div className="mt-4 rounded-md bg-red-50 border border-red-200 p-3 text-[12px] text-red-800"
           data-testid="coa-pull-error">
        {String(pull.error)}
      </div>
    )}
  </div>
);

// Card shown at the top of the TB tab — operator picks a ledger + period and
// either pastes an OTBI/BIP report URL (recommended) OR supplies a list of
// account combinations. We call the appropriate backend mode and aggregate
// rows into a Vakuai TB.
const OracleTbPullCard = ({ ledgers, onLoaded }) => {
  const [form, setForm] = useState({
    mode: "report",          // "report" | "accounts"
    ledger_id: "", period: "", report_url: "",
    accounts_text: "",
    amount_type: "PTD", currency: "", period_name: "",
    force_refresh: false,
    catalog_root: "/Custom",
  });
  const [state, setState] = useState({ loading: false, result: null, error: null });
  const [catalog, setCatalog] = useState({ loading: false, reports: [], error: null, elapsed_s: 0 });
  const browseCatalog = async () => {
    setCatalog({ loading: true, reports: [], error: null, elapsed_s: 0 });
    try {
      const { data } = await api.get("/epm/oracle/bip-reports",
                                       { params: { root: form.catalog_root || "/Custom",
                                                    depth: 2 } });
      setCatalog({ loading: false, reports: data.reports || [],
                    error: null, elapsed_s: data.elapsed_s || 0 });
    } catch (e) {
      setCatalog({ loading: false, reports: [],
                    error: e?.response?.data?.detail || e.message, elapsed_s: 0 });
    }
  };
  const pull = async () => {
    setState({ loading: true, result: null, error: null });
    try {
      if (!form.ledger_id || !form.period) {
        setState({ loading: false, result: null,
                   error: "Pick a ledger and enter a period (YYYY-MM)." });
        return;
      }
      const payload = { ledger_id: form.ledger_id, period: form.period };
      if (form.mode === "report") {
        if (!form.report_url.trim()) {
          setState({ loading: false, result: null,
                     error: "Paste the OTBI / BIP report URL or pick one from the dropdown." });
          return;
        }
        payload.report_url = form.report_url.trim();
      } else {
        const accounts = form.accounts_text.split(/[,\n]+/).map((s) => s.trim()).filter(Boolean);
        if (accounts.length === 0) {
          setState({ loading: false, result: null,
                     error: "Add at least one account combination." });
          return;
        }
        payload.accounts = accounts;
        payload.amount_type = form.amount_type || "PTD";
      }
      if (form.currency)     payload.currency = form.currency;
      if (form.period_name)  payload.period_name = form.period_name;
      if (form.force_refresh) payload.force_refresh = true;
      const { data } = await api.post("/epm/trial-balances/pull-oracle", payload);
      setState({ loading: false, result: data, error: null });
      onLoaded && onLoaded();
    } catch (e) {
      setState({ loading: false, result: null,
                 error: e?.response?.data?.detail || e.message });
    }
  };
  const oracleLedgers = ledgers.filter((l) => l.source === "oracle" && l.external_id);
  return (
    <div className="rounded-sm border border-[#0B1E3F]/20 bg-gradient-to-br from-[#0B1E3F]/[0.04] to-white shadow-sm p-5"
         data-testid="tb-oracle-pull">
      <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-[#0B1E3F] mb-1">
        Oracle Fusion · live connector
      </div>
      <h3 className="font-bold tracking-tight mb-2">Pull Trial Balance from Fusion</h3>
      {oracleLedgers.length === 0 ? (
        <p className="text-[12px] text-slate-600">
          No Oracle-linked ledger found. Run <strong>Pull from Oracle</strong> on the
          Chart of Accounts tab first to import ledgers.
        </p>
      ) : (
        <>
          {/* Mode toggle */}
          <div className="inline-flex border border-slate-300 rounded overflow-hidden mb-4"
               data-testid="tb-pull-mode-toggle">
            {[["report", "OTBI / BIP report URL"], ["accounts", "Per-account list"]].map(([k, l]) => (
              <button key={k} onClick={() => setForm({ ...form, mode: k })}
                      data-testid={`tb-pull-mode-${k}`}
                      className={`px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider transition-colors ${
                        form.mode === k
                          ? "bg-[#0B1E3F] text-white"
                          : "bg-white text-slate-600 hover:bg-slate-50"
                      }`}>{l}</button>
            ))}
          </div>
          <div className="grid grid-cols-4 gap-3 mb-3">
            <select value={form.ledger_id} onChange={(e) => setForm({ ...form, ledger_id: e.target.value })}
                    data-testid="tb-oracle-ledger"
                    className="border border-slate-300 px-2 py-1.5 text-sm bg-white">
              <option value="">— Oracle ledger —</option>
              {oracleLedgers.map((l) => (
                <option key={l.id} value={l.id}>{l.name} ({l.currency})</option>
              ))}
            </select>
            <input placeholder="period YYYY-MM" value={form.period}
                   onChange={(e) => setForm({ ...form, period: e.target.value })}
                   data-testid="tb-oracle-period"
                   className="border border-slate-300 px-2 py-1.5 text-sm" />
            <input placeholder="currency override (optional)" value={form.currency}
                   onChange={(e) => setForm({ ...form, currency: e.target.value.toUpperCase() })}
                   className="border border-slate-300 px-2 py-1.5 text-sm uppercase" maxLength={3} />
            <input placeholder="period label (e.g. Jan-25, optional)" value={form.period_name}
                   onChange={(e) => setForm({ ...form, period_name: e.target.value })}
                   className="border border-slate-300 px-2 py-1.5 text-sm" />
          </div>

          {form.mode === "report" ? (
            <>
              {/* Auto-discovery: browse the BIP catalog and pick a report. */}
              <div className="rounded border border-dashed border-[#0B1E3F]/30 bg-white/60 p-3 mb-3"
                   data-testid="tb-bip-catalog">
                <div className="flex items-center gap-2 mb-2">
                  <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Catalog root</label>
                  <input value={form.catalog_root}
                         onChange={(e) => setForm({ ...form, catalog_root: e.target.value })}
                         data-testid="tb-catalog-root"
                         className="border border-slate-300 px-2 py-1 text-[12px] font-mono w-64" />
                  <button onClick={browseCatalog} disabled={catalog.loading}
                          data-testid="tb-browse-catalog-btn"
                          className="ml-2 inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider text-[#0B1E3F] border border-[#0B1E3F] px-3 py-1 hover:bg-slate-50 disabled:opacity-60">
                    {catalog.loading ? "Browsing…" : "Browse Fusion catalog"}
                  </button>
                </div>
                {catalog.reports.length > 0 && (
                  <select onChange={(e) => setForm({ ...form, report_url: e.target.value })}
                          value={form.report_url}
                          data-testid="tb-catalog-dropdown"
                          className="w-full border border-slate-300 px-2 py-1.5 text-[12px] bg-white">
                    <option value="">— pick a TB report ({catalog.reports.length} found in {catalog.elapsed_s}s) —</option>
                    {catalog.reports.map((r) => (
                      <option key={r.absolute_path} value={r.run_url}>
                        {r.name}  ·  {r.absolute_path}
                      </option>
                    ))}
                  </select>
                )}
                {!catalog.loading && catalog.reports.length === 0 && (catalog.error || catalog.elapsed_s > 0) && (
                  <div className={`text-[11px] ${catalog.error ? "text-red-700" : "text-slate-500"}`}>
                    {catalog.error
                      ? String(catalog.error)
                      : `No TB-looking reports found under ${form.catalog_root}. Drill into a sub-folder and try again.`}
                  </div>
                )}
              </div>
              <input placeholder="https://erp.example.com/xmlpserver/services/rest/v1/reports/.../run"
                     value={form.report_url}
                     onChange={(e) => setForm({ ...form, report_url: e.target.value })}
                     data-testid="tb-oracle-report-url"
                     className="w-full border border-slate-300 px-2 py-1.5 text-[12px] font-mono mb-2" />
              <div className="text-[10px] text-slate-500 mb-3">
                Pick from the dropdown above or paste any OTBI / BIP report
                URL that returns a flat TB (CSV, JSON, or BIP-XML). Headers
                matched case-insensitively (account / debit / credit + optional
                company, cost_center, intercompany, description).
              </div>
            </>
          ) : (
            <>
              <textarea placeholder="Account combinations — one per line, e.g. 01-001-1110-00-000-000"
                        value={form.accounts_text} rows={4}
                        onChange={(e) => setForm({ ...form, accounts_text: e.target.value })}
                        data-testid="tb-oracle-accounts"
                        className="w-full border border-slate-300 px-2 py-1.5 text-[12px] font-mono mb-2" />
              <div className="flex items-center gap-3 mb-3">
                <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Amount</label>
                <select value={form.amount_type} onChange={(e) => setForm({ ...form, amount_type: e.target.value })}
                        className="border border-slate-300 px-2 py-1 text-[12px] bg-white">
                  <option value="PTD">PTD · Period</option>
                  <option value="QTD">QTD · Quarter</option>
                  <option value="YTD">YTD · Year</option>
                </select>
                <label className="ml-auto inline-flex items-center gap-2 text-[11px] cursor-pointer select-none"
                       data-testid="tb-force-refresh-label">
                  <input type="checkbox" checked={form.force_refresh}
                         onChange={(e) => setForm({ ...form, force_refresh: e.target.checked })}
                         data-testid="tb-force-refresh"
                         className="accent-[#0B1E3F]" />
                  <span className="text-slate-700">Force refresh (bypass 1-hour cache)</span>
                </label>
              </div>
            </>
          )}

          <div className="flex items-center gap-2">
            <span className="text-[10px] text-slate-500">
              {form.mode === "report"
                ? "Sends one authenticated GET to your report URL."
                : `Detail mode · ${form.currency || "ledger currency"} · ${form.amount_type}` +
                  (form.force_refresh ? " · cache bypassed" : " · cached accounts reused")}
            </span>
            <button onClick={pull} disabled={state.loading}
                    data-testid="tb-pull-oracle-btn"
                    className="ml-auto bg-[#0B1E3F] text-white px-4 py-2 text-[11px] font-bold uppercase tracking-wider disabled:opacity-60">
              {state.loading ? "Pulling…" : "Pull from Oracle"}
            </button>
          </div>
        </>
      )}
      {state.result && (
        <div className="mt-4 rounded-md bg-emerald-50 border border-emerald-200 p-3 text-[12px]"
             data-testid="tb-pull-success">
          <div>
            <span className="font-semibold text-emerald-800">Pulled </span>
            <span className="font-mono text-emerald-900">{state.result.line_count}</span>
            <span className="text-emerald-800"> line{state.result.line_count === 1 ? "" : "s"} ({state.result.fetch_mode}) · </span>
            <span className="text-emerald-800">DR </span>
            <span className="font-mono text-emerald-900">{state.result.total_debit?.toFixed(2)}</span>
            <span className="text-emerald-800"> · CR </span>
            <span className="font-mono text-emerald-900">{state.result.total_credit?.toFixed(2)}</span>
            <span className="text-emerald-800"> · </span>
            <span className={state.result.balanced ? "font-semibold text-emerald-800" : "font-semibold text-red-700"}>
              {state.result.balanced ? "balanced" : "UNBALANCED"}
            </span>
          </div>
          {/* Incremental-refresh diff vs the previous pull. */}
          {state.result.diff && (state.result.previous_pull_at || state.result.cache_reused > 0) && (
            <div className="mt-2 flex flex-wrap items-center gap-3 text-[11px]"
                 data-testid="tb-pull-diff">
              <span className="text-emerald-800 font-semibold">Δ since last pull:</span>
              <DiffPill label="new"       value={state.result.diff.new}       tone="emerald" />
              <DiffPill label="changed"   value={state.result.diff.changed}   tone="amber" />
              <DiffPill label="unchanged" value={state.result.diff.unchanged} tone="slate" />
              <DiffPill label="removed"   value={state.result.diff.removed}   tone="red" />
              {state.result.cache_reused > 0 && (
                <span className="text-[10px] text-slate-500">
                  · {state.result.cache_reused} account{state.result.cache_reused === 1 ? "" : "s"} served from cache
                </span>
              )}
            </div>
          )}
          {(state.result.fetch_errors || []).length > 0 && (
            <div className="mt-2 text-[11px] text-amber-800">
              {state.result.fetch_errors.length} warning{state.result.fetch_errors.length === 1 ? "" : "s"} —
              first: <span className="font-mono">{state.result.fetch_errors[0].error}</span>
            </div>
          )}
        </div>
      )}
      {state.error && (
        <div className="mt-4 rounded-md bg-red-50 border border-red-200 p-3 text-[12px] text-red-800"
             data-testid="tb-pull-error">
          {String(state.error)}
        </div>
      )}
    </div>
  );
};

// ------------------------------------------------------------- Segment Vals --
const ValuesTab = () => {
  const [coas, setCoas] = useState([]);
  const [selected, setSelected] = useState("");
  const [values, setValues] = useState([]);
  const [draft, setDraft] = useState({ segment_key: "", code: "", description: "" });
  useEffect(() => { api.get("/epm/coa").then((r) => { setCoas(r.data); if (r.data[0]) setSelected(r.data[0].id); }); }, []);
  const load = () => selected && api.get(`/epm/coa/${selected}/values`).then((r) => setValues(r.data));
  useEffect(() => { load(); }, [selected]);
  const coa = coas.find((c) => c.id === selected);
  const add = async () => { if (!draft.code || !draft.segment_key) return; await api.post(`/epm/coa/${selected}/values`, draft); setDraft({ ...draft, code: "", description: "" }); load(); };
  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <label className="text-[10px] tracking-[0.2em] uppercase font-bold text-slate-500">COA</label>
        <select value={selected} onChange={(e) => setSelected(e.target.value)} data-testid="values-coa" className="border border-slate-300 px-2 py-1.5 text-sm bg-white">
          {coas.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
      </div>
      {coa && (
        <div className="rounded-sm border border-slate-200 bg-white shadow-sm p-5">
          <h3 className="font-bold tracking-tight mb-3">Add segment value</h3>
          <div className="grid grid-cols-[1fr_1fr_2fr_60px] gap-2 items-center">
            <select value={draft.segment_key} onChange={(e) => setDraft({ ...draft, segment_key: e.target.value })} className="border border-slate-300 px-2 py-1.5 text-sm bg-white">
              <option value="">— segment —</option>
              {(coa.segments || []).map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
            </select>
            <input placeholder="code" value={draft.code} onChange={(e) => setDraft({ ...draft, code: e.target.value })} className="border border-slate-300 px-2 py-1.5 text-sm" />
            <input placeholder="description" value={draft.description} onChange={(e) => setDraft({ ...draft, description: e.target.value })} className="border border-slate-300 px-2 py-1.5 text-sm" />
            <button onClick={add} data-testid="values-add" className="bg-[#0B1E3F] text-white px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider">Add</button>
          </div>
        </div>
      )}
      <List title="Segment values" items={values} render={(v) => (
        <div className="flex items-center justify-between text-sm">
          <div><span className="font-mono text-[#0B1E3F]">{v.segment_key}</span> · <span className="font-mono">{v.code}</span> — {v.description}</div>
          <button onClick={async () => { await api.delete(`/epm/coa/${selected}/values/${v.id}`); load(); }} className="text-slate-400 hover:text-[#DC2626]"><Trash size={14} /></button>
        </div>
      )} />
    </div>
  );
};

// ------------------------------------------------------------------- Ledgers --
const LedgerTab = () => {
  const [items, setItems] = useState([]);
  const [coas, setCoas] = useState([]);
  const [draft, setDraft] = useState({ name: "", coa_id: "", currency: "USD", company_code: "", source: "manual" });
  const load = () => api.get("/epm/ledgers").then((r) => setItems(r.data));
  useEffect(() => { load(); api.get("/epm/coa").then((r) => setCoas(r.data)); }, []);
  const save = async () => { if (!draft.name || !draft.coa_id) return; await api.post("/epm/ledgers", draft); setDraft({ name: "", coa_id: draft.coa_id, currency: "USD", company_code: "", source: "manual" }); load(); };
  return (
    <div className="space-y-6">
      <div className="rounded-sm border border-slate-200 bg-white shadow-sm p-5">
        <h3 className="font-bold tracking-tight mb-3">Add ledger</h3>
        <div className="grid grid-cols-5 gap-2">
          <input placeholder="name" value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} data-testid="ledger-name" className="border border-slate-300 px-2 py-1.5 text-sm" />
          <select value={draft.coa_id} onChange={(e) => setDraft({ ...draft, coa_id: e.target.value })} data-testid="ledger-coa" className="border border-slate-300 px-2 py-1.5 text-sm bg-white">
            <option value="">— COA —</option>
            {coas.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <input placeholder="currency" value={draft.currency} onChange={(e) => setDraft({ ...draft, currency: e.target.value })} className="border border-slate-300 px-2 py-1.5 text-sm uppercase" maxLength={3} />
          <input placeholder="company code" value={draft.company_code} onChange={(e) => setDraft({ ...draft, company_code: e.target.value })} className="border border-slate-300 px-2 py-1.5 text-sm" />
          <button onClick={save} data-testid="ledger-save" className="bg-[#0B1E3F] text-white px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider">Save</button>
        </div>
      </div>
      <List title="Ledgers" items={items} render={(l) => (
        <div className="flex items-center justify-between text-sm">
          <div>{l.name} <span className="text-[10px] font-mono text-slate-500">{l.currency} · {l.company_code || "—"} · {l.source}</span></div>
          <button onClick={async () => { await api.delete(`/epm/ledgers/${l.id}`); load(); }} className="text-slate-400 hover:text-[#DC2626]"><Trash size={14} /></button>
        </div>
      )} />
    </div>
  );
};

// -------------------------------------------------------------- Trial Balances --
const TbTab = () => {
  const [ledgers, setLedgers] = useState([]);
  const [items, setItems] = useState([]);
  const [draft, setDraft] = useState({ ledger_id: "", period: "2026-04", currency: "USD", source: "manual",
    lines: [{ company: "", cost_center: "", intercompany: "", account: "", sub_account: "", debit: 0, credit: 0 }] });
  const load = () => api.get("/epm/trial-balances").then((r) => setItems(r.data));
  useEffect(() => { load(); api.get("/epm/ledgers").then((r) => setLedgers(r.data)); }, []);
  const save = async () => { if (!draft.ledger_id || !draft.period) return; await api.post("/epm/trial-balances", draft); load(); };
  const onCsv = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const text = await file.text();
    const [header, ...rows] = text.split(/\r?\n/).filter(Boolean);
    const cols = header.split(",").map((c) => c.trim().toLowerCase().replace(/\s+/g, "_"));
    const lines = rows.map((r) => {
      const parts = r.split(","); const obj = {};
      cols.forEach((c, i) => { obj[c] = (parts[i] || "").trim(); });
      obj.debit = parseFloat(obj.debit || 0) || 0; obj.credit = parseFloat(obj.credit || 0) || 0;
      return obj;
    });
    setDraft({ ...draft, source: "csv", lines });
  };
  const upd = (i, k, v) => { const next = [...draft.lines]; next[i] = { ...next[i], [k]: v }; setDraft({ ...draft, lines: next }); };
  return (
    <div className="space-y-6">
      <OracleTbPullCard ledgers={ledgers} onLoaded={load} />
      <TbMonitorsCard ledgers={ledgers} />
      <div className="rounded-sm border border-slate-200 bg-white shadow-sm p-5">
        <h3 className="font-bold tracking-tight mb-3">Load / enter trial balance</h3>
        <div className="grid grid-cols-4 gap-3 mb-4">
          <select value={draft.ledger_id} onChange={(e) => setDraft({ ...draft, ledger_id: e.target.value })} data-testid="tb-ledger" className="border border-slate-300 px-2 py-1.5 text-sm bg-white">
            <option value="">— Ledger —</option>
            {ledgers.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
          </select>
          <input placeholder="period YYYY-MM" value={draft.period} onChange={(e) => setDraft({ ...draft, period: e.target.value })} data-testid="tb-period" className="border border-slate-300 px-2 py-1.5 text-sm" />
          <input placeholder="currency" value={draft.currency} onChange={(e) => setDraft({ ...draft, currency: e.target.value })} className="border border-slate-300 px-2 py-1.5 text-sm uppercase" maxLength={3} />
          <select value={draft.source} onChange={(e) => setDraft({ ...draft, source: e.target.value })} className="border border-slate-300 px-2 py-1.5 text-sm bg-white">
            <option value="manual">manual</option><option value="csv">csv</option><option value="oracle">oracle</option>
          </select>
        </div>
        <div className="flex items-center gap-2 mb-3">
          <label className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider border border-[#0B1E3F] text-[#0B1E3F] hover:bg-slate-50 cursor-pointer">
            <UploadSimple size={12} weight="bold" /> CSV upload
            <input type="file" accept=".csv" onChange={onCsv} className="hidden" data-testid="tb-csv" />
          </label>
          <span className="text-[10px] text-slate-500">Columns: company, cost_center, intercompany, account, sub_account, debit, credit</span>
        </div>
        <div className="overflow-x-auto border border-slate-200">
          <table className="w-full text-[12px]">
            <thead className="bg-slate-50">
              <tr>{["Company", "CC", "IC", "Account", "Sub", "Debit", "Credit", ""].map((h) => <th key={h} className="px-2 py-1.5 text-left text-[10px] uppercase font-bold text-slate-500">{h}</th>)}</tr>
            </thead>
            <tbody>
              {draft.lines.map((l, i) => (
                <tr key={i} className="border-t border-slate-100">
                  {["company", "cost_center", "intercompany", "account", "sub_account"].map((k) => (
                    <td key={k}><input value={l[k]} onChange={(e) => upd(i, k, e.target.value)} className="w-full px-1.5 py-1 border-0" /></td>
                  ))}
                  <td><input type="number" value={l.debit} onChange={(e) => upd(i, "debit", +e.target.value)} className="w-full px-1.5 py-1 border-0 text-right" /></td>
                  <td><input type="number" value={l.credit} onChange={(e) => upd(i, "credit", +e.target.value)} className="w-full px-1.5 py-1 border-0 text-right" /></td>
                  <td><button onClick={() => setDraft({ ...draft, lines: draft.lines.filter((_, j) => j !== i) })} className="text-slate-400 hover:text-[#DC2626]"><Trash size={12} /></button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex items-center gap-2 mt-3">
          <button onClick={() => setDraft({ ...draft, lines: [...draft.lines, { company: "", cost_center: "", intercompany: "", account: "", sub_account: "", debit: 0, credit: 0 }] })}
            className="text-[11px] font-bold uppercase tracking-wider text-[#0B1E3F] inline-flex items-center gap-1"><Plus size={12} /> Row</button>
          <button onClick={save} data-testid="tb-save" className="ml-auto bg-[#0B1E3F] text-white px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider">Save trial balance</button>
        </div>
      </div>
      <List title="Trial balances" items={items} render={(t) => (
        <div className="flex items-center justify-between text-sm">
          <div>
            {t.ledger_name} <span className="text-[10px] font-mono text-slate-500">{t.period} · {t.currency} · {t.source} · {t.line_count} lines</span>
            {t.balanced ? <CheckCircle size={12} weight="fill" className="inline ml-2 text-emerald-600" /> : <span className="ml-2 text-[10px] text-[#DC2626]">UNBALANCED</span>}
          </div>
          <button onClick={async () => { await api.delete(`/epm/trial-balances/${t.id}`); load(); }} className="text-slate-400 hover:text-[#DC2626]"><Trash size={14} /></button>
        </div>
      )} />
    </div>
  );
};

// ---------------------------------------------------- Consolidation Setup + Elims --
const ConsTab = () => {
  const [cons, setCons] = useState([]);
  const [elims, setElims] = useState([]);
  const [ledgers, setLedgers] = useState([]);
  const [draft, setDraft] = useState({ name: "", target_currency: "USD", fx_source: "manual", child_ledger_ids: [] });
  const [elim, setElim] = useState({ name: "", company_a: "", account_a: "", company_b: "", account_b: "" });
  const load = () => Promise.all([api.get("/epm/consolidation"), api.get("/epm/eliminations"), api.get("/epm/ledgers")])
    .then(([a, b, c]) => { setCons(a.data); setElims(b.data); setLedgers(c.data); });
  useEffect(() => { load(); }, []);
  const toggleLedger = (id) => setDraft({ ...draft, child_ledger_ids: draft.child_ledger_ids.includes(id)
    ? draft.child_ledger_ids.filter((x) => x !== id) : [...draft.child_ledger_ids, id] });
  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <div className="rounded-sm border border-slate-200 bg-white shadow-sm p-5">
        <h3 className="font-bold tracking-tight mb-3">Consolidation ledger</h3>
        <div className="space-y-2">
          <input placeholder="name" value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} data-testid="cons-name" className="w-full border border-slate-300 px-2 py-1.5 text-sm" />
          <div className="grid grid-cols-2 gap-2">
            <input placeholder="target currency" value={draft.target_currency} onChange={(e) => setDraft({ ...draft, target_currency: e.target.value })} className="border border-slate-300 px-2 py-1.5 text-sm uppercase" maxLength={3} />
            <select value={draft.fx_source} onChange={(e) => setDraft({ ...draft, fx_source: e.target.value })} className="border border-slate-300 px-2 py-1.5 text-sm bg-white">
              <option value="manual">manual fx</option><option value="oracle">oracle fx</option><option value="api">api fx</option>
            </select>
          </div>
          <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-slate-500 mt-3">Child ledgers</div>
          <div className="border border-slate-200 max-h-40 overflow-y-auto">
            {ledgers.map((l) => (
              <label key={l.id} className="flex items-center gap-2 px-3 py-1.5 border-b border-slate-100 text-sm hover:bg-slate-50 cursor-pointer">
                <input type="checkbox" checked={draft.child_ledger_ids.includes(l.id)} onChange={() => toggleLedger(l.id)} />
                {l.name} <span className="text-[10px] text-slate-500 font-mono ml-1">{l.currency}</span>
              </label>
            ))}
          </div>
          <button onClick={async () => { await api.post("/epm/consolidation", draft); setDraft({ name: "", target_currency: "USD", fx_source: "manual", child_ledger_ids: [] }); load(); }}
            data-testid="cons-save" disabled={!draft.name}
            className="w-full bg-[#0B1E3F] text-white px-3 py-2 text-[11px] font-bold uppercase tracking-wider disabled:opacity-60">Save consolidation</button>
        </div>
        <div className="mt-5 border-t border-slate-200 pt-4">
          <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-slate-500 mb-2">Existing</div>
          {cons.map((c) => (
            <div key={c.id} className="flex items-center justify-between text-sm py-1.5 border-b border-slate-100">
              <div>{c.name} <span className="text-[10px] font-mono text-slate-500">→ {c.target_currency} · {c.child_ledger_ids.length} ledgers</span></div>
              <button onClick={async () => { await api.delete(`/epm/consolidation/${c.id}`); load(); }} className="text-slate-400 hover:text-[#DC2626]"><Trash size={14} /></button>
            </div>
          ))}
        </div>
      </div>

      <div className="rounded-sm border border-slate-200 bg-white shadow-sm p-5">
        <h3 className="font-bold tracking-tight mb-3">Elimination rules</h3>
        <div className="space-y-2">
          <input placeholder="rule name" value={elim.name} onChange={(e) => setElim({ ...elim, name: e.target.value })} className="w-full border border-slate-300 px-2 py-1.5 text-sm" />
          <div className="grid grid-cols-2 gap-2">
            <input placeholder="company A" value={elim.company_a} onChange={(e) => setElim({ ...elim, company_a: e.target.value })} className="border border-slate-300 px-2 py-1.5 text-sm" />
            <input placeholder="account A" value={elim.account_a} onChange={(e) => setElim({ ...elim, account_a: e.target.value })} className="border border-slate-300 px-2 py-1.5 text-sm" />
            <input placeholder="company B" value={elim.company_b} onChange={(e) => setElim({ ...elim, company_b: e.target.value })} className="border border-slate-300 px-2 py-1.5 text-sm" />
            <input placeholder="account B" value={elim.account_b} onChange={(e) => setElim({ ...elim, account_b: e.target.value })} className="border border-slate-300 px-2 py-1.5 text-sm" />
          </div>
          <button onClick={async () => { await api.post("/epm/eliminations", elim); setElim({ name: "", company_a: "", account_a: "", company_b: "", account_b: "" }); load(); }}
            data-testid="elim-save" disabled={!elim.name}
            className="w-full bg-[#0B1E3F] text-white px-3 py-2 text-[11px] font-bold uppercase tracking-wider disabled:opacity-60">Add elimination</button>
        </div>
        <div className="mt-5 border-t border-slate-200 pt-4">
          <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-slate-500 mb-2">Active rules</div>
          {elims.map((r) => (
            <div key={r.id} className="flex items-center justify-between text-sm py-1.5 border-b border-slate-100">
              <div>{r.name} <span className="text-[10px] font-mono text-slate-500">{r.company_a}.{r.account_a} ↔ {r.company_b}.{r.account_b}</span></div>
              <button onClick={async () => { await api.delete(`/epm/eliminations/${r.id}`); load(); }} className="text-slate-400 hover:text-[#DC2626]"><Trash size={14} /></button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------- Run / Output --
const RunTab = () => {
  const [cons, setCons] = useState([]);
  const [selected, setSelected] = useState("");
  const [period, setPeriod] = useState("2026-04");
  const [fxJson, setFxJson] = useState('{"EUR": 1.08, "GBP": 1.27, "USD": 1.0}');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => { api.get("/epm/consolidation").then((r) => { setCons(r.data); if (r.data[0]) setSelected(r.data[0].id); }); }, []);
  const run = async () => {
    setBusy(true); setErr(""); setResult(null);
    try {
      const fx_rates = JSON.parse(fxJson);
      const { data } = await api.post(`/epm/consolidation/${selected}/run`, { period, fx_rates });
      setResult(data);
    } catch (e) { setErr(e?.response?.data?.detail || e.message || "Run failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="space-y-6">
      <div className="rounded-sm border border-slate-200 bg-white shadow-sm p-5">
        <h3 className="font-bold tracking-tight mb-3">Run consolidation</h3>
        <div className="grid grid-cols-[1fr_120px_2fr_120px] gap-2">
          <select value={selected} onChange={(e) => setSelected(e.target.value)} data-testid="run-cons" className="border border-slate-300 px-2 py-1.5 text-sm bg-white">
            {cons.map((c) => <option key={c.id} value={c.id}>{c.name} → {c.target_currency}</option>)}
          </select>
          <input value={period} onChange={(e) => setPeriod(e.target.value)} placeholder="period" data-testid="run-period" className="border border-slate-300 px-2 py-1.5 text-sm" />
          <input value={fxJson} onChange={(e) => setFxJson(e.target.value)} placeholder='FX rates JSON e.g. {"EUR": 1.08}' className="border border-slate-300 px-2 py-1.5 text-[12px] font-mono" />
          <button onClick={run} disabled={!selected || busy} data-testid="run-go"
            className="bg-[#0B1E3F] text-white px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider inline-flex items-center gap-1 disabled:opacity-60">
            <Play size={12} weight="fill" /> {busy ? "Running…" : "Run"}
          </button>
        </div>
        {err && <div className="mt-3 text-[12px] text-[#DC2626]">{err}</div>}
      </div>
      {result && (
        <div className="rounded-sm border border-slate-200 bg-white shadow-sm p-5" data-testid="run-result">
          <h3 className="font-bold tracking-tight mb-2">Consolidated output</h3>
          <div className="text-[12px] mb-3">
            <span className="font-bold">{result.target_currency}</span> · Period {result.period} ·
            DR <span className="font-mono">{result.total_debit.toLocaleString()}</span> ·
            CR <span className="font-mono">{result.total_credit.toLocaleString()}</span> ·
            {result.balanced ? <span className="text-emerald-600 font-bold ml-1">BALANCED</span> : <span className="text-[#DC2626] font-bold ml-1">UNBALANCED</span>}
          </div>
          {result.elimination_journal?.length > 0 && (
            <div className="mb-3 text-[11px] bg-[#0B1E3F]/5 border border-blue-200 p-2">
              <div className="font-bold uppercase tracking-wider mb-1">Eliminations applied</div>
              {result.elimination_journal.map((e, i) => <div key={i}>{e.rule} — {e.amount.toLocaleString()}</div>)}
            </div>
          )}
          <table className="w-full text-[12px] border border-slate-200">
            <thead className="bg-slate-50">
              <tr>{["Company", "Account", "Cost Center", "IC", "Debit", "Credit"].map((h) => <th key={h} className="px-2 py-1.5 text-left text-[10px] uppercase font-bold text-slate-500">{h}</th>)}</tr>
            </thead>
            <tbody>
              {result.lines.map((l, i) => (
                <tr key={i} className="border-t border-slate-100">
                  <td className="px-2 py-1">{l.company}</td><td className="px-2 py-1 font-mono">{l.account}</td>
                  <td className="px-2 py-1">{l.cost_center}</td><td className="px-2 py-1">{l.intercompany}</td>
                  <td className="px-2 py-1 text-right font-mono">{l.debit.toLocaleString()}</td>
                  <td className="px-2 py-1 text-right font-mono">{l.credit.toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------- Small helpers --
const Field = ({ label, children }) => (
  <div><div className="text-[10px] tracking-[0.18em] uppercase font-bold text-slate-500 mb-1">{label}</div>{children}</div>
);
const List = ({ title, items, render }) => (
  <div className="border border-slate-200 bg-white">
    <div className="bg-slate-50 px-4 py-2 text-[10px] tracking-[0.2em] uppercase font-bold text-slate-500 border-b border-slate-200">{title} ({items.length})</div>
    <div className="divide-y divide-gray-100">
      {items.length === 0 ? <div className="p-4 text-sm text-slate-500">None yet.</div> :
        items.map((it) => <div key={it.id} className="px-4 py-2 hover:bg-slate-50">{render(it)}</div>)}
    </div>
  </div>
);
